import { useEffect, useMemo, useState, type ReactNode } from "react"
import {
  Plus,
  UserCheck,
  UserX,
  Users as UsersIcon,
  ShieldCheck,
  BadgeCheck,
  Phone,
  ChevronRight,
  Sparkles,
} from "lucide-react"
import AddUserModal from "./AddUserModal"
import { useDispatch, useSelector } from "react-redux"
import { allStaffUsersData } from "../../store/actions"
import { toggleStatusUser } from "../../api/products/post"
import SubscriptionModal from "../../components/subscrptions/SubscriptionModal"
import { getCurrentPlan } from "../../utils/permissions"
import { runSubscriptionUpgrade } from "../../utils/subscriptionUpgrade"

export default function Users() {
  const userdata = useSelector((state: any) => state.UserReducer.userdata)
  const staffusersdata = useSelector((state: any) => state.UserReducer.staffusersdata)
  const staffusers = useMemo(() => staffusersdata?.data || [], [staffusersdata])
  const store = userdata?.store
  const user = userdata?.user
  const currentPlan = getCurrentPlan(userdata)
  const availablePlans = userdata?.availablePlans
  const currentBillingCycle = userdata?.subscription?.billingCycle || userdata?.user?.subscriptionBillingCycle || "monthly"

  const [userList, setUserList] = useState<any[]>(staffusers)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [openSubscription, setOpenSubscription] = useState(false)
  const [page, setPage] = useState(1)
  const [busyUserId, setBusyUserId] = useState("")

  const dispatch = useDispatch()
  const pagination = staffusersdata?.pagination || {}
  const limit = 6

  useEffect(() => {
    if (!store?._id) return
    dispatch(allStaffUsersData(store._id, page, limit))
  }, [dispatch, store?._id, page])

  useEffect(() => {
    setUserList(Array.isArray(staffusers) ? staffusers : [])
  }, [staffusers])

  const stats = useMemo(() => {
    const total = userList.length
    const admins = userList.filter((u: any) => u.role === "ADMIN").length
    const active = userList.filter((u: any) => u.active).length
    return {
      total,
      admins,
      active,
      inactive: Math.max(0, total - active),
    }
  }, [userList])

  const canAddUsers = currentPlan !== "FREE"

  const toggleStatus = async (id: string) => {
    if (!store?._id) return

    try {
      setBusyUserId(id)
      await toggleStatusUser(store._id, id)
      dispatch(allStaffUsersData(store._id, page, limit))
      setUserList((prev) =>
        prev.map((u: any) =>
          u._id === id ? { ...u, active: !u.active } : u
        )
      )
    } finally {
      setBusyUserId("")
    }
  }

  return (
    <>
      <div className="space-y-8">
        <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-cyan-600 via-sky-700 to-blue-900 p-6 text-white shadow-xl sm:p-7">
          <div className="pointer-events-none absolute -left-10 top-10 h-28 w-28 rounded-full bg-cyan-300/20 blur-3xl" />
          <div className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-blue-200/20 blur-3xl" />

          <div className="relative flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-2xl">
              <p className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white/90">
                <Sparkles size={12} />
                Team Access
              </p>
              <h1 className="mt-3 text-2xl font-bold sm:text-3xl">Utilisateurs & permissions</h1>
              <p className="mt-2 text-sm text-white/85">
                Pilotez les accès de votre boutique, distinguez les administrateurs des vendeurs
                et gardez une vue claire sur les comptes actifs.
              </p>
            </div>

            <button
              onClick={() => {
                if (canAddUsers) setIsModalOpen(true)
                else setOpenSubscription(true)
              }}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 font-semibold text-blue-700 transition hover:bg-blue-50 sm:w-auto"
            >
              <Plus size={18} />
              {canAddUsers ? "Ajouter un utilisateur" : "Ajouter un utilisateur (upgrade requis)"}
            </button>
          </div>
        </section>

        {!canAddUsers && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            Le plan FREE ne permet pas d’ajouter de nouveaux utilisateurs. Passez à un plan payant.
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label="Total utilisateurs"
            value={stats.total}
            hint="Comptes configurés"
            icon={<UsersIcon size={18} />}
            tone="from-sky-500 to-blue-700"
          />
          <KpiCard
            label="Administrateurs"
            value={stats.admins}
            hint="Accès complet"
            icon={<ShieldCheck size={18} />}
            tone="from-emerald-500 to-teal-700"
          />
          <KpiCard
            label="Comptes actifs"
            value={stats.active}
            hint="Utilisateurs opérationnels"
            icon={<BadgeCheck size={18} />}
            tone="from-amber-500 to-orange-600"
          />
          <KpiCard
            label="Comptes inactifs"
            value={stats.inactive}
            hint="Accès désactivés"
            icon={<UserX size={18} />}
            tone="from-rose-500 to-red-600"
          />
        </div>

        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-800">Équipe de la boutique</p>
              <p className="mt-1 text-xs text-slate-500">
                Consultez les rôles, l’état des comptes et les actions disponibles.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              <UsersIcon size={14} />
              {stats.total} utilisateur{stats.total > 1 ? "s" : ""}
            </div>
          </div>

          <div className="hidden lg:block">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr className="text-left">
                  <th className="px-6 py-3 font-medium">Utilisateur</th>
                  <th className="px-6 py-3 font-medium">Contact</th>
                  <th className="px-6 py-3 font-medium">Rôle</th>
                  <th className="px-6 py-3 font-medium">Statut</th>
                  <th className="px-6 py-3 text-right font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {userList.map((u: any) => {
                  const isBusy = busyUserId === u._id
                  const initials = String(u.fullName || "U")
                    .split(" ")
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((part: string) => part[0]?.toUpperCase())
                    .join("")

                  return (
                    <tr key={u._id} className="border-t border-slate-100 transition hover:bg-slate-50/70">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-100 to-blue-100 text-sm font-semibold text-blue-700">
                            {initials || "U"}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">{u.fullName}</p>
                            <p className="text-xs text-slate-500">
                              {u.role === "ADMIN" ? "Supervision de la boutique" : "Gestion quotidienne des ventes"}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-slate-600">
                        <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1">
                          <Phone size={14} />
                          {u.phone}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <RoleBadge role={u.role} />
                      </td>

                      <td className="px-6 py-4">
                        <StatusBadge active={!!u.active} />
                      </td>

                      <td className="px-6 py-4 text-right">
                        {u.role !== "ADMIN" ? (
                          <button
                            onClick={() => toggleStatus(u._id)}
                            disabled={isBusy}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {isBusy ? (
                              <span className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" />
                            ) : u.active ? (
                              <UserX size={15} />
                            ) : (
                              <UserCheck size={15} />
                            )}
                            {isBusy ? "Mise à jour..." : u.active ? "Désactiver" : "Activer"}
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-2 text-xs font-medium text-slate-400">
                            Compte principal
                            <ChevronRight size={14} />
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}

                {userList.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                      Aucun utilisateur trouvé pour cette boutique.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="space-y-4 p-4 lg:hidden">
            {userList.map((u: any) => {
              const isBusy = busyUserId === u._id
              const initials = String(u.fullName || "U")
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((part: string) => part[0]?.toUpperCase())
                .join("")

              return (
                <article
                  key={u._id}
                  className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-100 to-blue-100 text-sm font-semibold text-blue-700">
                        {initials || "U"}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{u.fullName}</p>
                        <p className="mt-1 text-xs text-slate-500">{u.phone}</p>
                      </div>
                    </div>
                    <RoleBadge role={u.role} />
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl bg-white/80 px-3 py-3">
                    <StatusBadge active={!!u.active} />
                    {u.role !== "ADMIN" ? (
                      <button
                        onClick={() => toggleStatus(u._id)}
                        disabled={isBusy}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:opacity-60"
                      >
                        {isBusy ? (
                          <span className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" />
                        ) : u.active ? (
                          <UserX size={15} />
                        ) : (
                          <UserCheck size={15} />
                        )}
                        {isBusy ? "Mise à jour..." : u.active ? "Désactiver" : "Activer"}
                      </button>
                    ) : (
                      <span className="text-xs font-medium text-slate-400">Compte principal</span>
                    )}
                  </div>
                </article>
              )
            })}

            {userList.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
                Aucun utilisateur trouvé pour cette boutique.
              </div>
            )}
          </div>
        </section>

        {pagination?.totalPages > 1 && (
          <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-center text-sm text-slate-500 sm:text-left">
              Page <strong className="text-slate-700">{pagination.page}</strong> sur{" "}
              <strong className="text-slate-700">{pagination.totalPages}</strong>
            </p>

            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-xl border border-slate-200 px-4 py-2 transition hover:bg-slate-100 disabled:opacity-50"
              >
                Précédent
              </button>

              <button
                disabled={page === pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-xl bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      <AddUserModal
        currentStore={store}
        isOpen={isModalOpen && canAddUsers}
        onClose={() => setIsModalOpen(false)}
        onAdd={(newUser: any) => {
          dispatch(allStaffUsersData(store._id, page, limit))
          setUserList((prev: any[]) => [newUser, ...prev])
        }}
      />
      <SubscriptionModal
        isOpen={openSubscription}
        currentPlan={currentPlan}
        currentBillingCycle={currentBillingCycle}
        plans={availablePlans}
        onClose={() => setOpenSubscription(false)}
        onUpgrade={(planKey, billingCycle) =>
          runSubscriptionUpgrade({
            planKey,
            billingCycle,
            currentPlan,
            currentBillingCycle,
            user,
            dispatch,
            onClose: () => setOpenSubscription(false),
          })
        }
      />
    </>
  )
}

function KpiCard({
  label,
  value,
  hint,
  icon,
  tone,
}: {
  label: string
  value: number
  hint: string
  icon: ReactNode
  tone: string
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="mt-2 text-3xl font-bold text-slate-800">{value}</p>
          <p className="mt-1 text-xs text-slate-500">{hint}</p>
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${tone} text-white shadow-lg`}>
          {icon}
        </div>
      </div>
    </div>
  )
}

function RoleBadge({ role }: { role: string }) {
  const isAdmin = role === "ADMIN"

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
        isAdmin ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"
      }`}
    >
      {isAdmin ? <ShieldCheck size={14} /> : <UsersIcon size={14} />}
      {isAdmin ? "Administrateur" : "Vendeur"}
    </span>
  )
}

function StatusBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
      <UserCheck size={14} />
      Actif
    </span>
  ) : (
    <span className="inline-flex items-center gap-2 rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
      <UserX size={14} />
      Inactif
    </span>
  )
}
